import React from "react";
import PropTypes from "prop-types";
import { Box, Menu, MenuItem, Tooltip, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { ChevronDown, ChevronUp } from "lucide-react";

import { Button } from "./Button";

const resolveSx = (sx, context) => {
  if (typeof sx === "function") {
    return sx(context);
  }
  return sx;
};

export function ViewModeSelector({
  options = [],
  value,
  onChange,
  ariaLabel = "Seleccionar vista",
  tooltipTitle,
  buttonSx,
  menuPaperSx,
  menuItemSx,
}) {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const isOpen = Boolean(anchorEl);
  const selectedOption = React.useMemo(
    () => options.find((option) => option.value === value) || options[0],
    [options, value]
  );

  if (!selectedOption) return null;

  const SelectedIcon = selectedOption.icon;

  const selectorButton = (
    <Button
      variant="outline"
      size="sm"
      onClick={(event) => setAnchorEl(event.currentTarget)}
      aria-label={ariaLabel}
      sx={resolveSx(buttonSx, { theme, isOpen })}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          width: "100%",
          justifyContent: "space-between",
          whiteSpace: "nowrap",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          {SelectedIcon ? <SelectedIcon size={14} /> : null}
          <Typography
            component="span"
            sx={{
              fontSize: "0.8125rem",
              fontWeight: 500,
              lineHeight: 1,
            }}
          >
            {selectedOption.label}
          </Typography>
        </Box>
        {isOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
      </Box>
    </Button>
  );

  return (
    <>
      {tooltipTitle ? (
        <Tooltip title={tooltipTitle} arrow>
          <Box>{selectorButton}</Box>
        </Tooltip>
      ) : (
        selectorButton
      )}

      <Menu
        anchorEl={anchorEl}
        open={isOpen}
        onClose={() => setAnchorEl(null)}
        PaperProps={{
          sx: {
            mt: 0.5,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: "8px",
            boxShadow: theme.shadows[3],
            overflow: "hidden",
            ...resolveSx(menuPaperSx, { theme, isOpen }),
          },
        }}
      >
        {options.map((option) => {
          const OptionIcon = option.icon;
          const isSelected = option.value === value;

          return (
            <MenuItem
              key={option.value}
              selected={isSelected}
              onClick={() => {
                onChange(option.value);
                setAnchorEl(null);
              }}
              sx={{
                px: 1.5,
                py: 1,
                fontSize: "0.875rem",
                fontFamily: theme.typography.fontFamily,
                display: "flex",
                alignItems: "center",
                gap: 1,
                transition: "background-color 0.2s ease",
                "&:hover": {
                  backgroundColor: theme.palette.action.hover,
                },
                "&.Mui-selected": {
                  backgroundColor: theme.palette.grey[100],
                  fontWeight: 500,
                },
                "&.Mui-selected:hover": {
                  backgroundColor: theme.palette.action.hover,
                },
                ...resolveSx(menuItemSx, { theme, isOpen, isSelected, option }),
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {OptionIcon ? <OptionIcon size={16} /> : null}
                {option.label}
              </Box>
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}

ViewModeSelector.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
      icon: PropTypes.elementType,
    })
  ),
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  ariaLabel: PropTypes.string,
  tooltipTitle: PropTypes.string,
  buttonSx: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  menuPaperSx: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  menuItemSx: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
};
